import React, { useState, Component } from 'react';
import { ArrowLeft, Share2, MessageCircle, Copy, Check, DollarSign, Clock, ChevronRight } from 'lucide-react';
// Indian Rupee formatting helper
const formatINR = (amount: number): string => {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
};
interface BillSplitScreenProps {
  participants: Array<{
    id: string;
    name: string;
    items: Array<{
      name: string;
      price: number;
    }>;
    total: number;
  }>;
  orderTotal: number;
  tax: number;
  storeName: string;
  onBack: () => void;
}
export function BillSplitScreen({
  participants,
  orderTotal,
  tax,
  storeName,
  onBack
}: BillSplitScreenProps) {
  const [paymentStatus, setPaymentStatus] = useState<Record<string, boolean>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleMarkPaid = (participantId: string) => {
    setPaymentStatus(prev => ({
      ...prev,
      [participantId]: !prev[participantId]
    }));
  };
  const handleShareWhatsApp = (participant: {
    name: string;
    total: number;
  }) => {
    const message = encodeURIComponent(`Hey ${participant.name}! 👋\n\nYour share for our ${storeName} order is ${formatINR(participant.total)}.\n\nPay via UPI: magicpatterns@upi\n\nThanks! 🙏`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };
  const handleCopyUPI = (participantId: string, amount: number) => {
    const upiLink = `upi://pay?pa=magicpatterns@upi&pn=MagicPatterns&am=${Math.round(amount)}&cu=INR`;
    navigator.clipboard.writeText(upiLink);
    setCopiedId(participantId);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const totalPaid = participants.filter(p => paymentStatus[p.id]).reduce((sum, p) => sum + p.total, 0);
  const totalPending = orderTotal - totalPaid;
  const paidCount = Object.values(paymentStatus).filter(Boolean).length;
  return <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-24" data-id="element-149">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-5 py-6 sticky top-0 z-10" data-id="element-150">
        <div className="flex items-center gap-3 mb-4" data-id="element-151">
          <button onClick={onBack} className="w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-152">
            <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-153" />
          </button>
          <div data-id="element-154">
            <h1 className="text-2xl font-bold text-white" data-id="element-155">Split Bill</h1>
            <p className="text-sm text-purple-100" data-id="element-156">{storeName}</p>
          </div>
        </div>

        {/* Progress Summary */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4" data-id="element-157">
          <div className="flex items-center justify-between mb-3" data-id="element-158">
            <div data-id="element-159">
              <p className="text-sm text-purple-100" data-id="element-160">Total Order</p>
              <p className="text-3xl font-bold text-white" data-id="element-161">
                {formatINR(orderTotal)}
              </p>
            </div>
            <div className="text-right" data-id="element-162">
              <p className="text-sm text-purple-100" data-id="element-163">Paid</p>
              <p className="text-2xl font-bold text-[#E6FF00]" data-id="element-164">
                {paidCount}/{participants.length}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-white/20 rounded-full h-2 overflow-hidden" data-id="element-165">
            <div className="bg-[#E6FF00] h-full transition-all duration-500 rounded-full" style={{
            width: `${totalPaid / orderTotal * 100}%`
          }} data-id="element-166" />
          </div>

          <div className="flex justify-between mt-2 text-xs text-purple-100" data-id="element-167">
            <span data-id="element-168">Collected: {formatINR(totalPaid)}</span>
            <span data-id="element-169">Pending: {formatINR(totalPending)}</span>
          </div>
        </div>
      </div>

      <div className="px-5 py-6 space-y-4" data-id="element-170">
        {/* Participant Cards */}
        {participants.map(participant => {
        const isPaid = paymentStatus[participant.id];
        const isCopied = copiedId === participant.id;
        return <div key={participant.id} className={`rounded-2xl border-2 transition-all ${isPaid ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200'}`} data-id="element-171">
              {/* Header */}
              <div className="p-4 border-b border-gray-100" data-id="element-172">
                <div className="flex items-center justify-between mb-3" data-id="element-173">
                  <div className="flex items-center gap-3" data-id="element-174">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${isPaid ? 'bg-emerald-500 text-white' : 'bg-[#FFFBE6] text-gray-900'}`} data-id="element-175">
                      {participant.name[0].toUpperCase()}
                    </div>
                    <div data-id="element-176">
                      <h3 className="font-bold text-lg" data-id="element-177">{participant.name}</h3>
                      <p className={`text-sm ${isPaid ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-178">
                        {isPaid ? '✓ Paid' : 'Pending payment'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-179">
                    <p className="text-2xl font-bold text-[#A020F0]" data-id="element-180">
                      {formatINR(participant.total)}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-181">
                      {participant.items.length} item
                      {participant.items.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Items Breakdown */}
                <div className="bg-gray-50 rounded-xl p-3 space-y-2" data-id="element-182">
                  {participant.items.map((item, idx) => <div key={idx} className="flex justify-between text-sm" data-id="element-183">
                      <span className="text-gray-700" data-id="element-184">{item.name}</span>
                      <span className="font-medium text-gray-900" data-id="element-185">
                        {formatINR(item.price)}
                      </span>
                    </div>)}
                  <div className="border-t border-gray-200 pt-2 flex justify-between text-xs" data-id="element-186">
                    <span className="text-gray-500" data-id="element-187">GST (included)</span>
                    <span className="text-gray-600" data-id="element-188">
                      {formatINR(participant.total / orderTotal * tax)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 space-y-2" data-id="element-189">
                {!isPaid ? <>
                    {/* Request Payment Buttons */}
                    <div className="grid grid-cols-2 gap-2" data-id="element-190">
                      <button onClick={() => handleShareWhatsApp(participant)} className="flex items-center justify-center gap-2 py-3 bg-[#25D366] text-white rounded-xl font-medium hover:bg-[#20BA5A] transition-all active:scale-95" data-id="element-191">
                        <MessageCircle size={18} data-id="element-192" />
                        WhatsApp
                      </button>
                      <button onClick={() => handleCopyUPI(participant.id, participant.total)} className="flex items-center justify-center gap-2 py-3 bg-[#FFFC00] text-black rounded-xl font-medium hover:bg-[#F7C300] transition-all active:scale-95" data-id="element-193">
                        {isCopied ? <>
                            <Check size={18} data-id="element-194" />
                            Copied!
                          </> : <>
                            <Copy size={18} data-id="element-195" />
                            UPI Link
                          </>}
                      </button>
                    </div>

                    {/* Mark as Paid */}
                    <button onClick={() => handleMarkPaid(participant.id)} className="w-full py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:border-emerald-500 hover:text-emerald-600 transition-all" data-id="element-196">
                      Mark as Paid
                    </button>
                  </> : <button onClick={() => handleMarkPaid(participant.id)} className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-all flex items-center justify-center gap-2" data-id="element-197">
                    <Check size={18} data-id="element-198" />
                    Payment Received
                  </button>}
              </div>
            </div>;
      })}

        {/* Summary Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white mt-6" data-id="element-199">
          <h3 className="text-lg font-bold mb-4" data-id="element-200">Payment Summary</h3>
          <div className="space-y-3" data-id="element-201">
            <div className="flex justify-between" data-id="element-202">
              <span className="text-purple-100" data-id="element-203">Subtotal</span>
              <span className="font-bold" data-id="element-204">{formatINR(orderTotal - tax)}</span>
            </div>
            <div className="flex justify-between" data-id="element-205">
              <span className="text-purple-100" data-id="element-206">GST (5%)</span>
              <span className="font-bold" data-id="element-207">{formatINR(tax)}</span>
            </div>
            <div className="border-t border-white/20 pt-3 flex justify-between text-xl" data-id="element-208">
              <span className="font-bold" data-id="element-209">Total</span>
              <span className="font-bold" data-id="element-210">{formatINR(orderTotal)}</span>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-211">
          <div className="flex gap-3" data-id="element-212">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-213">
              <Clock size={16} className="text-white" data-id="element-214" />
            </div>
            <div data-id="element-215">
              <h4 className="font-bold text-blue-900 mb-1" data-id="element-216">Quick Tip</h4>
              <p className="text-sm text-blue-700" data-id="element-217">
                Share payment requests via WhatsApp or copy UPI links. Mark
                payments as received once you get them!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}